import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {CheckCircle, XCircle, AlertTriangle, History} from 'lucide-react'
import {useNavigate, Link} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {useEmbeddedListProps} from '@/hooks/use-embedded-list-props'
import {RestrictionRuleControllerRulesEndpoint} from '@/types/endpoint-contracts'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Button} from '@/components/ui/button'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Icon} from '@/components/shared/Icon'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'

interface RestrictionRulesProps {
    embedded?: boolean
    ownedByMe?: boolean
    emptyStateTitle?: string
}

export function RestrictionRules({embedded, ownedByMe, emptyStateTitle}: RestrictionRulesProps = {}) {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.restrictionRules.pageTitle)
    const navigate = useNavigate()
    const {hasPermission} = useAuth()
    const canViewDetections = hasPermission(permissions.restrictionRules.view) && !ownedByMe
    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        sortBy, sortDir,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: RestrictionRuleControllerRulesEndpoint,
        sortable: true,
        defaultSortBy: RestrictionRuleControllerRulesEndpoint.sort.NAME,
        defaultSortDir: SortDir.ASC,
    })

    // Issue 342.07 — empty URL shows ALL rules. The previous implementation
    // had separate `search` + `activeOnly` controls and a Clear button; the
    // unified bar now carries every active filter as a chip. The activeOnly
    // toggle is exposed through the filter panel as the boolean filter
    // declared on RestrictionRuleControllerRulesEndpoint.
    const {listParams, title, emptyMessage} = useEmbeddedListProps({
        contract: RestrictionRuleControllerRulesEndpoint,
        ownedByMe,
        baseQueryString: queryString,
        emptyStateTitle,
        defaultTitle: i18n.restrictionRules.allRules,
        createPermission: permissions.restrictionRules.view,
    })

    const {data: rulesData} = useQuery({
        queryKey: ['restriction-rules', queryString, page, pageSize, sortBy, sortDir, ownedByMe],
        queryFn: () => services.restrictionRules.list(listParams),
    })

    const rules = rulesData?.content || []
    const totalItems = rulesData?.totalElements || 0
    const totalPages = rulesData?.totalPages || 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.restrictionRule}
            title={title}
            count={totalItems}
            description={`${i18n.restrictionRules.ruleCount({count: totalItems})}${hasActiveFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
            data-testid="restriction-rules-list"
            embedded={embedded}
            actions={
                canViewDetections ? (
                    <Button
                        variant="outline"
                        onClick={() => navigate(routes.accessGovernance.restrictionRules.detections())}
                        data-testid="btn-detection-history"
                    >
                        <Icon icon={History} size="md" className="mr-2"/>
                        {i18n.restrictionRules.detectionHistoryBtn}
                    </Button>
                ) : undefined
            }
            filters={
                <UnifiedSearchBar
                    contract={RestrictionRuleControllerRulesEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.restrictionRules.searchByName}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                        <TableHead data-testid="col-denied-profiles">{i18n.restrictionRules.deniedProfiles}</TableHead>
                        <TableHead data-testid="col-violations">{i18n.restrictionRules.violations}</TableHead>
                        <TableHead data-testid="col-owner">{i18n.restrictionRules.owner}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rules.map((rule) => (
                        <ClickableTableRow key={rule.id}
                                  to={routes.accessGovernance.restrictionRules.detail(rule.id)}
                                  data-testid={`row-rule-${rule.id}`}>
                            <TableCell className="font-medium">
                                <TextLink
                                    to={routes.accessGovernance.restrictionRules.detail(rule.id)}
                                    stopPropagation
                                    testId={`link-rule-${rule.id}`}
                                >
                                    {rule.name}
                                </TextLink>
                                {rule.description && (
                                    <MutedText size="sm" block className="truncate max-w-xs">
                                        {rule.description}
                                    </MutedText>
                                )}
                            </TableCell>
                            <TableCell data-testid={`rule-status-${rule.id}`}>
                                {rule.active ? (
                                    <Badge variant={BadgeVariant.SUCCESS} className="gap-1 w-fit">
                                        <Icon icon={CheckCircle} size="xs"/>
                                        {i18n.common.active}
                                    </Badge>
                                ) : (
                                    <Badge variant={BadgeVariant.SECONDARY} className="gap-1 w-fit">
                                        <Icon icon={XCircle} size="xs"/>
                                        {i18n.common.inactive}
                                    </Badge>
                                )}
                            </TableCell>
                            <TableCell data-testid={`rule-profile-count-${rule.id}`}>
                                <Badge variant="outline">{rule.profileCount}</Badge>
                            </TableCell>
                            <TableCell data-testid={`rule-violation-count-${rule.id}`}>
                                <Link to={routes.accessGovernance.restrictionRules.violationsForRule(rule.id)} data-testid={`link-violations-${rule.id}`}>
                                    {rule.openViolationCount > 0 ? (
                                        <Badge variant="destructive" className="gap-1 w-fit">
                                            <Icon icon={AlertTriangle} size="xs"/>
                                            {rule.openViolationCount}
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline">0</Badge>
                                    )}
                                </Link>
                            </TableCell>
                            <TableCell data-testid={`rule-owner-${rule.id}`}>
                                {rule.owners.map(o => o.ownerPartyName).join(', ') || '-'}
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {rules.length === 0 && (
                        <TableEmptyState
                            colSpan={5}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.restrictionRules.noRulesMatch}
                            emptyMessage={emptyMessage ?? i18n.restrictionRules.noRulesFound}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
